import { memo, useEffect, useRef, useState } from 'react';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const TWITTER_WIDGETS_SRC = 'https://platform.twitter.com/widgets.js';
// Hard cap on the hydration skeleton, so a blocked / slow widgets.js never leaves it spinning forever.
const HYDRATE_TIMEOUT_MS = 6000;
// Builds the standard embed blockquote that widgets.js upgrades into a rich tweet. The `<a href>` is
// all twitter needs (no numeric id), so the normalized tweet URL works directly.
const buildBlockquote = (tweetUrl) => (`<blockquote class="twitter-tweet" data-theme="dark" data-align="center"><a href="${tweetUrl}"></a></blockquote>`);
// Shared redesign twitter / X embed (the redesign equivalent of the legacy TwitterEmbeddedContent),
// reused by the dare page and the review pages' RedesignVideoPlayer. Prefers the server oEmbed HTML;
// otherwise synthesizes the standard `.twitter-tweet` blockquote from the tweet URL alone (no numeric
// id, so it works for old / oEmbed-failed deliveries). Either markup is injected via
// `dangerouslySetInnerHTML` (same trust model: our own server HTML, or our own normalized URL) and
// hydrated with widgets.js. A Skeleton sized to the frame covers it until the rich embed iframe appears
// (or a timeout elapses), so there is no empty flash and zero CLS inside the fixed-height video box.
// Lives in common and must NOT import from `_pages`.
export const RedesignTwitterEmbed = memo(({ embeddedContent, tweetUrl }) => {
    const containerRef = useRef(null);
    const [hydrated, setHydrated] = useState(false);
    const markup = embeddedContent || (tweetUrl ? buildBlockquote(tweetUrl) : '');
    useEffect(() => {
        var _a;
        if (!markup) {
            return undefined;
        }
        setHydrated(false);
        const container = containerRef.current;
        const widgets = window.twttr;
        if ((_a = widgets === null || widgets === void 0 ? void 0 : widgets.widgets) === null || _a === void 0 ? void 0 : _a.load) {
            widgets.widgets.load(container !== null && container !== void 0 ? container : undefined);
        }
        else if (!document.querySelector(`script[src="${TWITTER_WIDGETS_SRC}"]`)) {
            const script = document.createElement('script');
            script.setAttribute('src', TWITTER_WIDGETS_SRC);
            script.setAttribute('async', 'true');
            document.head.appendChild(script);
        }
        // widgets.js replaces the blockquote with a rendered iframe; reveal once that child appears.
        let settled = false;
        const reveal = () => {
            if (settled) {
                return;
            }
            settled = true;
            setHydrated(true);
        };
        const observer = container
            ? new MutationObserver(() => {
                if (container.querySelector('iframe')) {
                    reveal();
                }
            })
            : undefined;
        if (container && observer) {
            observer.observe(container, { childList: true, subtree: true });
            if (container.querySelector('iframe')) {
                reveal();
            }
        }
        const timeout = setTimeout(reveal, HYDRATE_TIMEOUT_MS);
        return () => {
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            clearTimeout(timeout);
        };
    }, [markup]);
    if (!markup) {
        return null;
    }
    return (<div className="relative h-full w-full bg-background">
			{!hydrated && <Skeleton className="absolute inset-0 rounded-none"/>}
			<div ref={containerRef} className={cn('flex h-full w-full justify-center overflow-y-auto p-4 transition-opacity duration-300 [&_.twitter-tweet]:!my-0', hydrated ? 'opacity-100' : 'opacity-0')} 
    /* eslint-disable react/no-danger */
    dangerouslySetInnerHTML={{ __html: markup }}/>
		</div>);
});
RedesignTwitterEmbed.displayName = 'RedesignTwitterEmbed';
